import { useState, useEffect } from 'react'
import { Button, Input } from '../common'
import type { Client, ClientInsert, ClientUpdate } from '../../types/database.types'

export interface ClientFormData {
  clientData: ClientInsert | ClientUpdate
  sendInvitation: boolean
}

interface ClientFormProps {
  client?: Client | null
  providerId: string
  onSubmit: (data: ClientFormData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function ClientForm({
  client,
  providerId,
  onSubmit,
  onCancel,
  isLoading = false,
}: ClientFormProps) {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [notes, setNotes] = useState('')
  const [sendInvitation, setSendInvitation] = useState(true)
  const [error, setError] = useState('')

  const isEditing = !!client

  useEffect(() => {
    if (client) {
      setFullName(client.full_name)
      setEmail(client.email)
      setPhone(client.phone || '')
      setNotes(client.notes || '')
    }
  }, [client])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!fullName.trim()) {
      setError('Full name is required')
      return
    }

    if (!email.trim()) {
      setError('Email is required')
      return
    }

    try {
      if (isEditing) {
        await onSubmit({
          clientData: {
            full_name: fullName.trim(),
            email: email.trim(),
            phone: phone.trim() || null,
            notes: notes.trim() || null,
          } as ClientUpdate,
          sendInvitation: false, // Don't send on edit
        })
      } else {
        await onSubmit({
          clientData: {
            provider_id: providerId,
            full_name: fullName.trim(),
            email: email.trim(),
            phone: phone.trim() || null,
            notes: notes.trim() || null,
            status: 'invited',
          } as ClientInsert,
          sendInvitation,
        })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save client')
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      <Input
        label="Full Name"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
        placeholder="John Smith"
        required
      />

      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="john@example.com"
        required
        disabled={isEditing}
        helperText={isEditing ? 'Email cannot be changed' : undefined}
      />

      <Input
        label="Phone (optional)"
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="+1 (555) 123-4567"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal notes about this client..."
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="mt-1 text-sm text-gray-500">
          These notes are only visible to you
        </p>
      </div>

      {/* Send invitation email checkbox - only for new clients */}
      {!isEditing && (
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="sendInvitation"
            checked={sendInvitation}
            onChange={(e) => setSendInvitation(e.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="sendInvitation" className="text-sm">
            <span className="font-medium text-gray-700">
              Send invitation email
            </span>
            <p className="text-gray-500">
              The client will receive an email with a link to access their assigned programs
            </p>
          </label>
        </div>
      )}

      <div className="flex justify-end gap-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {isEditing ? 'Save Changes' : 'Add Client'}
        </Button>
      </div>
    </form>
  )
}
